import { useAuth } from "@clerk/clerk-react";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import logoImg from "assets/logos/logo-transparent.png";
import { BrandsContext } from "contexts/Brands";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import {
  BENTO_RESOURCES_GUIDE_LINK,
  LANDING_PAGE_LINK,
} from "constants/influencer";
import InboxSearchBarMenu from "features/Influencer/ContactList/NewOutreachTable/SearchBar/menu";
import { useOutreachTable } from "features/Influencer/ContactList/useOutreachTable";
import DiscoverSearchBar from "features/Influencer/Discover/Search";
import { isDemoUser } from "utils/demo";
import { isPage } from "utils/general";
import {
  DISPLAY_SCHEDULED_DIALOG,
  ON_BEHALF_OF_TOKEN,
  SCHEDULED_DATE_EMAIL,
} from "utils/localStorage";
import { generateInitials } from "utils/string";
import { trackEvent } from "utils/tracking";
import { usePageSize } from "utils/usePageSize";

import BugReportDialog from "../LeftSidebar/BugReportDialog";
import ReferralDialogNavigation from "../ReferralDialog/navigation";
import ChatHistory from "./ChatHistory";
import EmailCount from "./EmailCount";
import TopNavigationMobile from "./mobile";
import { Page, PageMenu } from "./schema";
import styles from "./styles";

function TopNavigation() {
  const { isSmallScreen: isOutreachTableSmallScreen } = useOutreachTable();
  const theme = useTheme();
  const location = useLocation();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const onBehalfToken = sessionStorage.getItem(ON_BEHALF_OF_TOKEN);

  const { signOut } = useAuth();
  const navigate = useNavigate();

  const { clearOrganizationContext, currentUser, currentOrg } = useContext(
    OrganizationUserContext,
  );
  const { userIntegration } = useContext(UserIntegrationsContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const { navigationWidth } = usePageSize();
  const { setUpgradeDialogSource } = useContext(SubscriptionContext);

  const [navigationReferDialog, setNavigationReferDialog] = useState(false);

  const showMobileNavigation =
    isMobileScreen || (isMediumScreen && discoverTab !== null);

  const { clearBrandContext } = useContext(BrandsContext);

  const [openBugDialog, setOpenBugDialog] = useState(false);

  const handleReportBug = () => {
    setOpenBugDialog(true);
    trackEvent("Bug Report Dialog Shown", {
      Source: "Profile Menu",
    });
  };

  const [profileAnchor, setProfileAnchor] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    isProfileAnchor: boolean,
  ) => {
    setProfileAnchor(isProfileAnchor);
    trackEvent("Settings Menu Dropdown Clicked");
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const trackPageNavigation = (route: string) => {
    if (route === Page.BENTO_GUIDE) {
      trackEvent("How To Use Bento Link Clicked");
    } else {
      trackEvent("Page Navigated", { route });
    }
    handleClose();
  };

  const handleLogOut = () => {
    trackEvent("Logout Button Clicked");
    if (onBehalfToken) {
      sessionStorage.removeItem(ON_BEHALF_OF_TOKEN);
      window.open("/admin", "_self");
    } else if (currentOrg?.isBrand) {
      signOut(() => navigate(`/brands/sign-in`, { replace: true }));
    } else {
      signOut(() => navigate(`/sign-in`, { replace: true }));
    }

    // Reset all contexts.
    clearBrandContext();
    clearOrganizationContext();

    // Reset localStorage
    localStorage.removeItem(`${DISPLAY_SCHEDULED_DIALOG}-${currentOrg?.id}`);
    localStorage.removeItem(`${SCHEDULED_DATE_EMAIL}-${currentOrg?.id}`);
  };

  const goToLandingPage = () => {
    trackEvent("Landing Page Logo Clicked", {
      "Clicked From": "Navigation Bar",
    });
    window.open(LANDING_PAGE_LINK, "_blank");
  };

  const handleOpenReferral = () => {
    trackEvent("Refer A Friend Clicked", {
      "Clicked From": "Navigation Bar",
    });
    setNavigationReferDialog(true);
  };

  const profileMenu = (
    <Grid
      item
      container
      xs="auto"
      justifyContent="flex-end"
      alignItems="center"
    >
      <ButtonBase onClick={(e) => handleClick(e, true)}>
        <Avatar sx={styles.avatar}>
          {generateInitials(currentUser?.name || currentUser?.email || "")}
        </Avatar>
      </ButtonBase>
    </Grid>
  );

  const getPagesToDisplay = () => {
    let pages: string[] = [];
    if (!profileAnchor) {
      if (currentOrg?.isBrand) {
        pages = [Page.INBOX, Page.TRACKING, Page.CAMPAIGNS];
      } else {
        pages = [
          Page.HOME,
          Page.BRANDS,
          Page.RECOMMENDATIONS,
          Page.INBOX,
          Page.TRACKING,
          Page.CAMPAIGNS,
          Page.LOCAL,
        ];
      }
    }
    if (currentOrg?.isVaUser && !currentOrg?.isBrand) {
      pages.push(Page.VA);
    }
    if (!showMobileNavigation || profileAnchor) {
      if (currentOrg?.isBrand) {
        pages = [...pages, Page.SETTINGS, Page.TEMPLATES];
      } else {
        pages = [
          ...pages,
          Page.SETTINGS,
          Page.TEMPLATES,
          Page.PROFILE,
          Page.BENTO_GUIDE,
        ];
      }
    }
    return pages;
  };

  return (
    <>
      {showMobileNavigation ? (
        <TopNavigationMobile
          width={navigationWidth}
          handleClick={handleClick}
          profileMenu={profileMenu}
        />
      ) : (
        <Grid
          sx={[
            styles.navigation,
            {
              width: navigationWidth,
            },
          ]}
          gap={1}
          justifyContent="space-between"
        >
          <Grid item xs="auto">
            <Grid container alignItems="center">
              <Box
                component="img"
                src={logoImg}
                sx={styles.topBarLogo}
                onClick={goToLandingPage}
              />

              {!currentOrg?.isBrand && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setUpgradeDialogSource("Navigation Bar")}
                >
                  See Plans
                </Button>
              )}

              {!currentOrg?.isBrand && (
                <Button
                  size="small"
                  sx={{ textTransform: "none", color: "info.main", ml: 1 }}
                  onClick={handleOpenReferral}
                >
                  Refer a Friend 🎁
                </Button>
              )}
            </Grid>
          </Grid>

          <Grid item xs>
            {isPage(routes.brands) && <DiscoverSearchBar />}
            {isPage(routes.inbox) && !isOutreachTableSmallScreen && (
              <InboxSearchBarMenu />
            )}
          </Grid>

          {isPage(routes.home) && isDemoUser(currentOrg) && (
            <Grid item xs="auto" sx={{ mr: 5 }}>
              <ChatHistory />
            </Grid>
          )}
          <Grid
            container
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
            item
            xs="auto"
          >
            <EmailCount />
          </Grid>
          {profileMenu}
        </Grid>
      )}

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Box sx={styles.name}>{currentUser?.name}</Box>
        <Box sx={styles.email}>{currentUser?.email}</Box>
        {userIntegration?.email &&
          userIntegration?.email !== currentUser?.email && (
            <Box sx={styles.email}>
              Emails are sent from this account:
              <br /> {userIntegration?.email}
            </Box>
          )}

        <Divider sx={{ mt: 1 }} />
        {getPagesToDisplay()?.map((page) => (
          <Link
            to={
              page === Page.BENTO_GUIDE
                ? BENTO_RESOURCES_GUIDE_LINK
                : `/dashboard/${page}${location.search}`
            }
            style={{ textDecoration: "none" }}
            key={`page-${page}`}
            target={page === Page.BENTO_GUIDE ? "_blank" : "_self"}
          >
            <MenuItem key={page} onClick={() => trackPageNavigation(page)}>
              <ListItemIcon sx={styles.icon}>
                <Box component="i" className={PageMenu[page]?.icon} />
              </ListItemIcon>
              <ListItemText>{PageMenu[page]?.label}</ListItemText>
            </MenuItem>
          </Link>
        ))}
        <Divider />
        <MenuItem onClick={handleLogOut}>
          <ListItemIcon sx={styles.icon}>
            <Box component="i" className="fa-solid fa-sign-out" />
          </ListItemIcon>
          <ListItemText>Log Out</ListItemText>
        </MenuItem>
        {profileAnchor && (
          <MenuItem onClick={handleReportBug}>
            <ListItemIcon sx={styles.icon}>
              <Box component="i" className="fa-solid fa-comment" />
            </ListItemIcon>
            <ListItemText>Ask a Question</ListItemText>
          </MenuItem>
        )}

        <BugReportDialog
          open={openBugDialog}
          handleClose={() => setOpenBugDialog(false)}
        />
      </Menu>
      <ReferralDialogNavigation
        open={navigationReferDialog}
        close={() => setNavigationReferDialog(false)}
        source="Top Navigation Bar"
      />
    </>
  );
}

export default TopNavigation;
